<template>
  <v-container>
    <v-card>
      <v-card-title>
        Etiquetas
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="newName"
              label="Nombre"
              @keyup.enter="add"
            />
          </v-col>
          <v-col cols="12" md="2">
            <tag-color-picker v-model="newColor" :disabled="saving" />
          </v-col>
          <v-col cols="12" md="3">
            <payroll-fields v-model="newPayroll" :disabled="saving" />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              class="primary"
              :disabled="!newName || saving"
              @click="add"
            >Agregar</v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>

        <v-data-table
          :headers="headers"
          :items="tags"
          :loading="loading"
          no-data-text="No hay etiquetas cargadas"
          :footer-props="{ itemsPerPageOptions: [15, 50, 100], itemsPerPageText: 'Mostrar' }"
        >
          <template v-slot:item.color="{ item }">
            <v-chip small label :color="tagBackground(item)" :text-color="textColorFor(tagBackground(item))">
              {{ item.name }}
            </v-chip>
          </template>
          <template v-slot:item.payroll="{ item }">
            {{ payrollText(item) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon :disabled="saving" @click="startEdit(item)">
              <v-icon color="primary">mode_edit</v-icon>
            </v-btn>
            <v-btn icon :disabled="saving" @click="remove(item)">
              <v-icon color="error">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="editDialog" max-width="500" persistent>
      <v-card v-if="editing">
        <v-card-title>Editar etiqueta</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editing.name"
            label="Nombre"
            @keyup.enter="saveEdit"
          />
          <v-text-field
            v-model="editing.reportLabel"
            label="Texto representativo"
            hint="Representa a la etiqueta en correos y reportes. Vacío: se usa el nombre."
            persistent-hint
            :disabled="saving"
            @keyup.enter="saveEdit"
          />
          <v-radio-group v-model="editing.gender" label="Género" row dense hide-details :disabled="saving" class="mb-2">
            <v-radio label="Masculino" value="masculine" />
            <v-radio label="Femenino" value="feminine" />
          </v-radio-group>
          <tag-color-picker v-model="editing.color" :disabled="saving" />
          <payroll-fields v-model="editing.payroll" :disabled="saving" />
          <v-alert v-if="editErrorMessage" type="error" dense text>{{ editErrorMessage }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="saving" @click="cancelEdit">Cancelar</v-btn>
          <v-btn class="primary" :loading="saving" :disabled="!editing.name" @click="saveEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import tagsService from "@/services/TagsService";
import tagColorPicker from "@/components/common/tag-color-picker.vue";
import payrollFields from "@/components/views/tags/payroll-fields.component.vue";
import { tagBackground, textColorFor } from "@/utils/tag-color";

const noPayroll = () => ({ employees: false, vehicles: false });

export default {
  name: "list-tags",
  components: { tagColorPicker, payrollFields },
  data() {
    return {
      tags: [],
      loading: false,
      // Bandera "en vuelo": sin esto, un doble click dispara dos altas.
      saving: false,
      newName: null,
      newColor: "",
      newPayroll: noPayroll(),
      errorMessage: null,
      editDialog: false,
      editing: null,
      editErrorMessage: null,
      headers: [
        { text: "Nombre", value: "name" },
        { text: "Color", value: "color", sortable: false, width: 200 },
        { text: "Nómina", value: "payroll", sortable: false, width: 180 },
        { text: "Acciones", value: "actions", sortable: false, width: 120 }
      ]
    };
  },
  async mounted() {
    await this.load();
  },
  methods: {
    tagBackground,
    textColorFor,
    payrollText(tag) {
      const payroll = tag.payroll || {};
      return [payroll.employees && "Personal", payroll.vehicles && "Vehículos"]
        .filter(Boolean)
        .join(" y ");
    },
    async load() {
      this.errorMessage = null;
      this.loading = true;
      try {
        this.tags = await tagsService.list();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar las etiquetas");
      } finally {
        this.loading = false;
      }
    },
    // El backend manda el motivo real (nombre repetido, por ejemplo). Se
    // muestra tal cual y solo se cae al texto propio si no vino ninguno.
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async add() {
      if (!this.newName || this.saving) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        await tagsService.create({ name: this.newName, color: this.newColor, payroll: this.newPayroll });
        this.newName = null;
        this.newColor = "";
        this.newPayroll = noPayroll();
        await this.load();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo agregar la etiqueta");
      } finally {
        this.saving = false;
      }
    },
    startEdit(item) {
      this.editing = {
        _id: item._id,
        name: item.name,
        reportLabel: item.reportLabel || "",
        gender: item.gender || "masculine",
        color: item.color || "",
        payroll: Object.assign(noPayroll(), item.payroll)
      };
      this.editErrorMessage = null;
      this.editDialog = true;
    },
    cancelEdit() {
      this.editDialog = false;
      this.editing = null;
      this.editErrorMessage = null;
    },
    async saveEdit() {
      if (!this.editing || !this.editing.name || this.saving) return;
      this.editErrorMessage = null;
      this.saving = true;
      try {
        await tagsService.edit(this.editing._id, {
          name: this.editing.name,
          color: this.editing.color,
          payroll: this.editing.payroll,
          reportLabel: this.editing.reportLabel,
          gender: this.editing.gender
        });
        this.cancelEdit();
        await this.load();
      } catch (error) {
        this.editErrorMessage = this.messageOf(error, "No se pudo editar la etiqueta");
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      this.errorMessage = null;
      this.saving = true;
      try {
        await tagsService.remove(item._id);
        await this.load();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo borrar la etiqueta");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
